// Filter kolom seragam: token warna (merah/oren/ijo/lime/netral/kuning) +
// kondisi numerik (>=, <=, >, <, =, angka loose-format) + substring teks,
// digabung dengan operator `and` / `or` (WAJIB ada spasi di sisi operator,
// supaya kata seperti "orange" tidak terbaca sebagai "or").

export type ColorCategory = 'merah' | 'oren' | 'ijo' | 'lime' | 'netral' | 'kuning';

const HEX_TO_CAT: Record<string, ColorCategory> = {
  '#ff0000': 'merah',
  '#ffa500': 'oren',
  '#00ff00': 'ijo',
  '#b2ff00': 'lime',
  '#ffff00': 'kuning',
};

const TOKEN_TO_CAT: Record<string, ColorCategory> = {
  merah: 'merah',
  red: 'merah',
  oren: 'oren',
  orange: 'oren',
  ijo: 'ijo',
  hijau: 'ijo',
  green: 'ijo',
  lime: 'lime',
  netral: 'netral',
  neutral: 'netral',
  abu: 'netral',
  gray: 'netral',
  grey: 'netral',
  kuning: 'kuning',
  yellow: 'kuning',
};

// Normalisasi class CSS warna teks → kategori warna.
// null = tidak dikenali (bukan warna yang bisa difilter).
export const colorCategoryFromClass = (cls: string | null | undefined): ColorCategory | null => {
  if (!cls) return null;
  const lower = cls.toLowerCase();
  const hex = lower.match(/#[0-9a-f]{6}|#[0-9a-f]{3}(?![0-9a-f])/);
  if (hex) {
    let h = hex[0];
    if (h.length === 4) h = '#' + h[1] + h[1] + h[2] + h[2] + h[3] + h[3];
    const mapped = HEX_TO_CAT[h];
    if (mapped) return mapped;
  }
  if (/\bred\b|text-rose/.test(lower)) return 'merah';
  if (/\bpink\b/.test(lower)) return 'merah';
  if (/\borange\b/.test(lower)) return 'oren';
  if (/\blime\b/.test(lower)) return 'lime';
  if (/\bgreen\b/.test(lower)) return 'ijo';
  if (/\byellow\b|\bamber\b/.test(lower)) return 'kuning';
  if (/gray|slate|zinc|muted|white|foreground/.test(lower)) return 'netral';
  return null;
};

export const colorTokenCategory = (atom: string): ColorCategory | null =>
  TOKEN_TO_CAT[atom.trim().toLowerCase()] ?? null;

export const parseLooseNumber = (raw: string): number => {
  let s = raw.toUpperCase();
  let mult = 1;
  const suf = s.slice(-1);
  if (suf === 'K') { mult = 1e3; s = s.slice(0, -1); }
  else if (suf === 'M') { mult = 1e6; s = s.slice(0, -1); }
  else if (suf === 'B') { mult = 1e9; s = s.slice(0, -1); }
  else if (suf === 'T') { mult = 1e12; s = s.slice(0, -1); }
  const dot = s.lastIndexOf('.');
  const com = s.lastIndexOf(',');
  if (dot >= 0 && com >= 0) {
    if (dot > com) s = s.replace(/,/g, '');
    else s = s.replace(/\./g, '').replace(',', '.');
  } else if (/^-?\d{1,3}([.,]\d{3})+$/.test(s)) {
    s = s.replace(/[.,]/g, '');
  } else {
    s = s.replace(/,/g, '.');
  }
  const n = parseFloat(s);
  return isNaN(n) ? NaN : n * mult;
};

export interface ParsedCondition {
  operator: '>' | '>=' | '<' | '<=' | '=';
  target: number;
  // "=" toleran setengah digit terakhir angka yg diketik ("2.2" → 2.15–2.25),
  // supaya cocok dgn nilai yg tampil di layar, bukan kecocokan float persis.
  epsilon?: number;
}

export const parseNumericCondition = (condition: string): ParsedCondition | null => {
  const trimmed = condition.trim();
  if (!trimmed) return null;
  const match = trimmed.match(/^(>=|<=|>|<|=)?\s*(-?[\d.,]+(?:[TKMBtkmb])?)\s*%?$/i);
  if (!match) return null;
  const operator = (match[1] || '=') as '>' | '>=' | '<' | '<=' | '=';
  const rawNum = match[2] ?? '';
  const target = parseLooseNumber(rawNum);
  if (isNaN(target)) return null;
  // digit desimal setelah normalisasi loose-format ("2,20" → "2.2" → 1 digit)
  const mantissa = rawNum.replace(/[TKMBtkmb]$/i, '');
  const normalized = String(parseLooseNumber(mantissa));
  const decimals = normalized.includes('.') ? ((normalized.split('.')[1] ?? '').replace(/0+$/, '').length) : 0;
  const suffix = rawNum.match(/[TKMBtkmb]$/i)?.[0].toUpperCase();
  const mult = suffix === 'K' ? 1e3 : suffix === 'M' ? 1e6 : suffix === 'B' ? 1e9 : suffix === 'T' ? 1e12 : 1;
  const epsilon = 0.5 * Math.pow(10, -decimals) * mult;
  return { operator, target, epsilon };
};

export const evaluateCondition = (val: number, cond: ParsedCondition | null): boolean => {
  if (!cond) return true;
  switch (cond.operator) {
    case '>': return val > cond.target;
    case '>=': return val >= cond.target;
    case '<': return val < cond.target;
    case '<=': return val <= cond.target;
    case '=': return cond.epsilon !== undefined
      ? Math.abs(val - cond.target) <= cond.epsilon + Math.max(1e-9, Math.abs(cond.target) * 1e-12)
      : val === cond.target;
    default: return val === cond.target;
  }
};

export interface FilterCell {
  value: number;
  text: string;
  colorClass: string | null;
  isNumeric: boolean;
}

export interface CrossRefCondition {
  operator: '>' | '>=' | '<' | '<=' | '=';
  ref: string;
  arith?: { op: '+' | '-' | '*' | '/'; num: number };
}

// Contoh atom: `>d1`, `>=D1+5K`, `>(D1/2)`, `<(d1*2)`, `<(M1*50%)`.
// refRe harus anchored (mis. /^[cf]*[dw]\d+$/ atau /^(?:\d{6}|m\d{1,2})$/i).
export const parseCrossColumnReference = (
  raw: string,
  refRe: RegExp,
  opts?: { requireParens?: boolean },
): CrossRefCondition | null => {
  const m = raw.trim().match(/^(>=|<=|>|<|=)\s*(\()?([a-z0-9]+)\s*(?:([+\-*/])\s*(-?[\d.,]+(?:[TKMBtkmb])?%?))?\s*(\))?$/i);
  if (!m) return null;
  const hasOpen = Boolean(m[2]);
  const hasClose = Boolean(m[6]);
  if (hasOpen !== hasClose) return null;
  if (opts?.requireParens && !hasOpen) return null;
  const ref = (m[3] ?? '').toLowerCase();
  if (!refRe.test(ref)) return null;
  const operator = (m[1] ?? '=') as CrossRefCondition['operator'];
  if (m[4]) {
    let numRaw = m[5] ?? '';
    let scale = 1;
    if (numRaw.endsWith('%')) {
      numRaw = numRaw.slice(0, -1);
      scale = 100;
    }
    const num = parseLooseNumber(numRaw) / scale;
    if (isNaN(num)) return null;
    if (m[4] === '/' && num === 0) return null;
    return { operator, ref, arith: { op: m[4] as '+' | '-' | '*' | '/', num } };
  }
  return { operator, ref };
};

export const applyCrossColumnTarget = (refVal: number, cond: CrossRefCondition): number => {
  if (!cond.arith) return refVal;
  const { op, num } = cond.arith;
  if (op === '+') return refVal + num;
  if (op === '-') return refVal - num;
  if (op === '*') return refVal * num;
  return refVal / num;
};

export const evaluateCrossColumn = (val: number, refVal: number, cond: CrossRefCondition): boolean => {
  const target = applyCrossColumnTarget(refVal, cond);
  switch (cond.operator) {
    case '>': return val > target;
    case '>=': return val >= target;
    case '<': return val < target;
    case '<=': return val <= target;
    case '=': return val === target;
    default: return false;
  }
};

// Hook utk atom khusus (mis. referensi periode ">d0+5"). Return null = bukan milikmu.
export type AtomEvaluator = (atom: string, cell: FilterCell) => boolean | null;

// Nilai kolom lain dari namanya (mis. "d1", "w2"); undefined = bukan nama kolom.
export type RefResolver = (name: string) => number | undefined;

// ── Nilai absolut: `abs(...)` atau `|...|` (hanya kolom numerik) ─────────────
// Sisi kiri opsional, default `x` = nilai sel ini; bila sisi kanan memakai nilai absolut, sel ini ikut absolut:
//   `|x|>1B`, `abs(x)>1B`, `abs>1B`        → |sel| > 1B
//   `<|d0|`, `>abs(d1)`, `>(|d1|/2)`, `>|d1|+5B` → |sel| dibanding |D0| / |D1| (nama kolom lewat RefResolver)
//   `|d0|>|d1|`, `abs(x)>=abs(d1)`         → dua sisi absolut
//   `x<|d0|`                               → sel apa adanya (bertanda) dibanding |D0|
// Angka boleh pakai K/M/B/T; `50%` sesudah * atau / = 0,5, di tempat lain = 50 (seperti filter biasa).
type AbsExpr =
  | { k: 'num'; v: number }
  | { k: 'ref'; name: string }
  | { k: 'abs' | 'neg'; e: AbsExpr }
  | { k: 'bin'; op: '+' | '-' | '*' | '/'; a: AbsExpr; b: AbsExpr };

type CmpOp = '>' | '>=' | '<' | '<=' | '=';
const CMP_OPS: readonly string[] = ['>=', '<=', '>', '<', '='];

export interface AbsComparison { op: CmpOp; lhs: AbsExpr; rhs: AbsExpr }

const ABS_TOKEN = /\s*(>=|<=|[><=()|+\-*/]|\d[\d.,]*(?:[tkmb](?![a-z0-9]))?%?|[a-z][a-z0-9]*)/iy;

export const parseAbsComparison = (raw: string): AbsComparison | null => {
  if (!/abs|\|/i.test(raw)) return null;
  const toks: string[] = [];
  ABS_TOKEN.lastIndex = 0;
  const src = raw.trim();
  while (ABS_TOKEN.lastIndex < src.length) {
    const m = ABS_TOKEN.exec(src);
    if (!m) return null;
    toks.push(m[1]!);
  }
  let i = 0;
  const peek = () => toks[i];
  const take = () => toks[i++];
  const num = (t: string, pctIsFraction: boolean): AbsExpr | null => {
    let s = t;
    let scale = 1;
    if (s.endsWith('%')) { s = s.slice(0, -1); if (pctIsFraction) scale = 100; }
    const v = parseLooseNumber(s) / scale;
    return isNaN(v) ? null : { k: 'num', v };
  };
  const primary = (pctIsFraction = false): AbsExpr | null => {
    const t = take();
    if (t === undefined) return null;
    if (t === '(') { const e = sum(); return e && take() === ')' ? e : null; }
    if (t === '|') { const e = sum(); return e && take() === '|' ? { k: 'abs', e } : null; }
    if (t === '-') { const e = primary(pctIsFraction); return e && { k: 'neg', e }; }
    if (/^abs$/i.test(t)) {
      if (peek() !== '(') return { k: 'abs', e: { k: 'ref', name: 'x' } };  // `abs>1B` = |x| > 1B
      take();
      const e = sum();
      return e && take() === ')' ? { k: 'abs', e } : null;
    }
    if (/^\d/.test(t)) return num(t, pctIsFraction);
    if (/^[a-z]/i.test(t)) return { k: 'ref', name: t.toLowerCase() };
    return null;
  };
  const prod = (): AbsExpr | null => {
    let a = primary();
    while (a && (peek() === '*' || peek() === '/')) {
      const op = take() as '*' | '/';
      const b = primary(true);
      a = b && { k: 'bin', op, a, b };
    }
    return a;
  };
  function sum(): AbsExpr | null {
    let a = prod();
    while (a && (peek() === '+' || peek() === '-')) {
      const op = take() as '+' | '-';
      const b = prod();
      a = b && { k: 'bin', op, a, b };
    }
    return a;
  }
  const lhsOmitted = CMP_OPS.includes(peek() ?? '');
  let lhs: AbsExpr | null = { k: 'ref', name: 'x' };
  if (!lhsOmitted) lhs = sum();
  const op = take();
  if (!lhs || !op || !CMP_OPS.includes(op)) return null;
  const rhs = sum();
  if (!rhs || i !== toks.length) return null;
  // `<|d0|` reads as "|sel| < |D0|": an absolute value on the right makes the implicit left side absolute too
  if (lhsOmitted && hasAbs(rhs)) lhs = { k: 'abs', e: lhs };
  return { op: op as CmpOp, lhs, rhs };
};

const hasAbs = (e: AbsExpr): boolean =>
  e.k === 'abs' || (e.k === 'neg' && hasAbs(e.e)) || (e.k === 'bin' && (hasAbs(e.a) || hasAbs(e.b)));

const evalAbsExpr = (e: AbsExpr, x: number, resolve?: RefResolver): number | undefined => {
  switch (e.k) {
    case 'num': return e.v;
    case 'ref': return e.name === 'x' ? x : resolve?.(e.name);
    case 'abs': { const v = evalAbsExpr(e.e, x, resolve); return v === undefined ? v : Math.abs(v); }
    case 'neg': { const v = evalAbsExpr(e.e, x, resolve); return v === undefined ? v : -v; }
    case 'bin': {
      const a = evalAbsExpr(e.a, x, resolve);
      const b = evalAbsExpr(e.b, x, resolve);
      if (a === undefined || b === undefined) return undefined;
      return e.op === '+' ? a + b : e.op === '-' ? a - b : e.op === '*' ? a * b : a / b;
    }
  }
};

// null = tidak bisa dihitung (mis. nama kolom tak dikenal) → diabaikan seperti atom lain yg tak dikenali.
export const evaluateAbsComparison = (c: AbsComparison, x: number, resolve?: RefResolver): boolean | null => {
  const l = evalAbsExpr(c.lhs, x, resolve);
  const r = evalAbsExpr(c.rhs, x, resolve);
  if (l === undefined || r === undefined || !isFinite(r)) return null;
  switch (c.op) {
    case '>': return l > r;
    case '>=': return l >= r;
    case '<': return l < r;
    case '<=': return l <= r;
    default: return Math.abs(l - r) <= 1e-9 * Math.max(1, Math.abs(r));
  }
};

const evalAtom = (
  rawAtom: string,
  cell: FilterCell,
  customAtom?: AtomEvaluator,
  resolveRef?: RefResolver,
): boolean | null => {
  const atom = rawAtom.trim();
  if (!atom) return null;
  const cat = colorTokenCategory(atom);
  if (cat) return cat === colorCategoryFromClass(cell.colorClass);
  if (cell.isNumeric) {
    const abs = parseAbsComparison(atom);
    if (abs) return evaluateAbsComparison(abs, cell.value, resolveRef);
  }
  if (customAtom) {
    const r = customAtom(atom, cell);
    if (r !== null) return r;
  }
  if (cell.isNumeric) {
    const cond = parseNumericCondition(atom);
    if (cond) return evaluateCondition(cell.value, cond);
    return null;
  }
  return cell.text.includes(atom.toLowerCase());
};

const combineAnd = (results: (boolean | null)[]): boolean | null => {
  if (results.includes(false)) return false;
  if (results.includes(null)) return null;
  return true;
};

const combineOr = (results: (boolean | null)[]): boolean | null => {
  if (results.includes(true)) return true;
  if (results.includes(null)) return null;
  return false;
};

// Evaluasi ekspresi filter satu kolom.
// Grammar: expr := clause (" or " clause)* ; clause := atom (" and " atom)*
// Operator HARUS diberi spasi di dua sisi ("a and b", "a or b").
// Precedence: and > or. null (atom tak bisa dievaluasi) diabaikan dgn aman;
// hasil akhir null = filter tidak berlaku (lolos semua) — sama dgn perilaku lama.
export const evaluateFilterExpression = (
  expr: string,
  cell: FilterCell,
  opts?: { customAtom?: AtomEvaluator; resolveRef?: RefResolver },
): boolean => {
  const trimmed = expr.trim();
  if (!trimmed) return true;
  const orClauses = trimmed.split(/\s+or\s+/i);
  const orResults = orClauses.map(clause => {
    const atoms = clause.split(/\s+and\s+/i);
    return combineAnd(atoms.map(a => evalAtom(a, cell, opts?.customAtom, opts?.resolveRef)));
  });
  const result = combineOr(orResults);
  return result === null ? true : result;
};
